import { Stack, Switch } from '@mantine/core'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import {
	deleteNoti,
	editNoti,
	getListNoti,
	pushNoti,
} from 'src/apis/notificationAPI'
import CustomButton from 'src/components/CustomComponents/CustomButton'
import CustomClearInput from 'src/components/CustomComponents/CustomClearInput'
import CustomSelect from 'src/components/CustomComponents/CustomSelect'
import TableComponent from 'src/components/TableComponent'
import { useLoading } from 'src/context/LoadingContext'
import { useModal } from 'src/context/ModalContext'
import { NotificationData } from 'src/interfaces'
import { filterNormalIlike } from 'src/utils/filter'
import { formatDateTime } from 'src/variable/constant'
import ModalCreateNotification from './ModalCreateNotification'
import { IconEdit, IconPlus, IconSend, IconTrash } from '@tabler/icons-react'
import CustomTooltip from 'src/components/CustomComponents/CustomTooltip'
import actionClasses from 'src/components/TableComponent/TableComponent.module.scss'
import classes from './NotificationPageList.module.scss'

interface NotificationPageListProps {
	columns: MRT_ColumnDef[]
	fields: {
		value: string
		label: string
	}[]
	[key: string]: any
}
function NotificationPageList({ columns, fields }: NotificationPageListProps) {
	const { openError, openModal } = useModal()
	const { openLoadingContext, closeLoadingContext } = useLoading()
	const [data, setData] = useState<NotificationData[]>([])
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 1,
		pageSize: 10,
	})
	const [value, setValue] = useState({
		field: fields[0].value,
		value: null,
	})
	const [total, setTotal] = useState<number>(0)
	const [loading, setLoading] = useState<boolean>(false)
	const [open, setOpen] = useState({
		open: false,
		data: null as NotificationData,
	})

	const handleGetData = useCallback(async () => {
		let arr: NotificationData[] = []
		let count: number = 0
		let filter = {}
		setLoading(true)
		try {
			filter = filterNormalIlike(value)
			const res = await getListNoti({
				fields: ['$all'],
				page: pagination.pageIndex,
				limit: pagination.pageSize,
				where: { ...filter },
			})
			arr = res?.data?.results?.objects?.rows?.map((item, index) => ({
				...item,
				no: index + 1 + pagination.pageSize * (pagination.pageIndex - 1),
				created_at: dayjs(item?.created_at).format(formatDateTime),
			}))
			count = res?.data?.results?.objects?.count
		} catch (error) {
			openError(error)
		} finally {
			setData(arr)
			setTotal(count)

			setTimeout(() => {
				setLoading(false)
			}, 500)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(pagination), JSON.stringify(value)])
	const handleSetValue = (e, key) => {
		if (key === 'field' && value.value === '') {
			setValue((pre) => ({ ...pre, [key]: e, value: null }))
		} else {
			setValue((pre) => ({ ...pre, [key]: e }))
		}
	}

	const handleChangePage = useCallback((value: number) => {
		setPagination((pre) => ({
			...pre,
			pageIndex: value,
		}))
	}, [])
	const handleEditNoti = useCallback((data) => {
		const { original: NotificationData } = data
		setOpen({ open: true, data: NotificationData })
	}, [])
	const handleSendNoti = useCallback(
		async (id) => {
			openLoadingContext()
			try {
				const res = await pushNoti(id)
				if (res?.data?.code === 200) {
					openModal({
						type: 'success',
						label: `You have successfully sent this notification !`,
					})
				}
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	)
	const handleDeleteNoti = useCallback(
		async (id) => {
			openLoadingContext()
			try {
				const res = await deleteNoti(id)
				if (res?.data?.code === 200) {
					await handleGetData()
					openModal({
						type: 'success',
						label: `Notification has been deactivated successfully!`,
					})
				}
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[handleGetData],
	)
	const handleSend = useCallback(
		(row: any) => {
			openModal({
				type: 'confirm',
				label: <div>Do you want to send this notification ?</div>,
				handleConfirm: () => {
					handleSendNoti(row?.original?.id)
				},
			})
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	)
	const handleDelete = useCallback(
		(row: any) => {
			openModal({
				type: 'confirm',
				label: <div>Do you want to deactivate this notification?</div>,
				handleConfirm: () => {
					handleDeleteNoti(row?.original?.id)
				},
			})
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[handleDeleteNoti],
	)

	const handleUpdateStatus = useCallback(
		async (id: string, status: boolean) => {
			openLoadingContext()
			try {
				const res = await editNoti(id, { status })
				if (res) {
					setData((pre) =>
						pre.map((item) => (item?.id === id ? { ...item, status } : item)),
					)
					openModal({
						type: 'success',
						label: "Notification's status updated successfully!",
					})
				}
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[JSON.stringify(data)],
	)
	const handleConfirmUpdate = useCallback(
		(row: NotificationData) => {
			const { id, status } = row
			openModal({
				type: 'confirm',
				label: (
					<div>
						Do you want to {status ? 'inactive' : 'active'} this notification ?
					</div>
				),
				handleConfirm: () => {
					handleUpdateStatus(id, !status)
				},
			})
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[handleUpdateStatus],
	)
	const statusCol = useMemo(
		(): MRT_ColumnDef => ({
			accessorKey: 'status',
			header: 'Status',
			maxSize: 140,
			Cell: ({ cell, row }) => {
				const isActive = cell.getValue<boolean>()
				return (
					<div className={classes.statusCell}>
						<span
							className={`${classes.statusBadge} ${
								isActive ? classes.statusActive : classes.statusInactive
							}`}
						>
							{isActive ? 'Active' : 'Inactive'}
						</span>
						<Switch
							radius="sm"
							size="md"
							checked={isActive}
							onChange={() =>
								handleConfirmUpdate(row.original as NotificationData)
							}
						/>
					</div>
				)
			},
		}),
		[handleConfirmUpdate],
	)
	const columnsNew = useMemo(
		() => [...columns.slice(0, 3), statusCol, ...columns.slice(3)],
		[statusCol, columns],
	)
	useEffect(() => {
		handleGetData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(pagination)])
	useEffect(() => {
		let timer
		if (value.value !== null) {
			timer = setTimeout(() => {
				if (pagination.pageIndex === 1) {
					handleGetData()
				} else handleChangePage(1)
			}, 500)
		}
		return () => clearTimeout(timer)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(value)])

	const _renderCustomActions = useCallback(
		(row) => (
			<div className={classes.actionsGroup}>
				<CustomTooltip label="Edit notification" withinPortal withArrow>
					<button
						type="button"
						className={`${actionClasses.actionButton} ${actionClasses.defaultAction}`}
						onClick={() => handleEditNoti(row)}
					>
						<IconEdit size={16} />
					</button>
				</CustomTooltip>
				<CustomTooltip label="Send notification now" withinPortal withArrow>
					<button
						type="button"
						className={`${actionClasses.actionButton} ${actionClasses.primaryAction}`}
						onClick={() => handleSend(row)}
					>
						<IconSend size={16} />
					</button>
				</CustomTooltip>
				<CustomTooltip label="Deactivate notification" withinPortal withArrow>
					<button
						type="button"
						className={`${actionClasses.actionButton} ${actionClasses.dangerAction}`}
						onClick={() => handleDelete(row)}
					>
						<IconTrash size={16} />
					</button>
				</CustomTooltip>
			</div>
		),
		[handleDelete, handleEditNoti, handleSend],
	)
	const activeCount = useMemo(
		() => data.filter((item) => item.status).length,
		[data],
	)

	return (
		<Stack className={classes.page}>
			<div className={classes.header}>
				<div className={classes.headingGroup}>
					<h1 className={classes.title}>Notifications</h1>
					<p className={classes.subtitle}>
						Manage scheduled broadcasts and keep sent notifications in history.
					</p>
				</div>
				<div className={classes.stats}>
					<span className={classes.statPill}>{total} total</span>
					<span className={classes.activePill}>{activeCount} active on page</span>
				</div>
			</div>
			<div className={classes.toolbar}>
				<div className={classes.filters}>
					<CustomSelect
						value={value.field}
						data={fields}
						onChange={(e) => {
							handleSetValue(e, 'field')
						}}
						sx={{
							input: {
								color: 'black !important',
								fontWeight: 600,
							},
						}}
					/>
					<CustomClearInput
						value={value.value}
						onChange={(e) => handleSetValue(e, 'value')}
						placeholder="Search notifications"
					/>
				</div>

				<CustomButton
					variant="outlined"
					className={classes.createButton}
					startIcon={<IconPlus size={16} />}
					onClick={() => setOpen({ open: true, data: null })}
				>
					Create notification
				</CustomButton>
			</div>
			<TableComponent
				columns={columnsNew}
				total={total}
				data={data}
				pagination={pagination}
				loading={loading}
				handleChangePage={handleChangePage}
				// searchQueryValue={
				// 	typeof value.value === 'string' ? value.value : undefined
				// }
				// onSearchQueryInit={handleSearchQueryInit}
				// filterQueryValues={{
				// 	search_field: value.field,
				// }}
				// onFilterQueryInit={handleFilterQueryInit}
				_renderCustomActions={_renderCustomActions}
			/>
			{open.open && (
				<ModalCreateNotification
					open={true}
					data={open.data}
					onClose={() => setOpen({ open: false, data: null })}
					handleGetData={handleGetData}
					pagination={pagination}
					handleChangePage={handleChangePage}
				/>
			)}
		</Stack>
	)
}

export default memo(NotificationPageList)
